import React, { useState } from 'react'
import { Transition } from '@headlessui/react'
import { isAndroid, isIOS, isMobile } from 'react-device-detect'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { PlayCircleIcon } from '@/atoms/Icons/PlayCircleIcon'
import { Image } from '@/atoms/Image'
import { LazyHydrate } from '@/atoms/LazyHydrate'
import { AppleStoreDownloadLogo } from '@/atoms/Logos/AppleStoreDownloadLogo'
import { AppleStoreMinimalLogo } from '@/atoms/Logos/AppleStoreMinimalLogo'
import { AppleTvLogo } from '@/atoms/Logos/AppleTvLogo'
import { FireTVLogo } from '@/atoms/Logos/FireTVLogo'
import { GooglePlayMinimalLogo } from '@/atoms/Logos/GooglePlayMinimalLogo'
import { GoogleStoreDownloadBWLogo } from '@/atoms/Logos/GoogleStoreDownloadBWLogo'
import { GoogleTvLogoMono } from '@/atoms/Logos/GoogleTvLogoMono'
import { LgTvLogo } from '@/atoms/Logos/LgTvLogo'
import { MicrosoftXboxTextLogo } from '@/atoms/Logos/MicrosoftXboxTextLogo'
import { RokuLogo } from '@/atoms/Logos/RokuLogo'
import { SamsungLogo } from '@/atoms/Logos/SamsungLogo'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { CaptionMD, CaptionSM, HeadingXS, ParagraphSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { VideoModal } from '@/molecules/VideoModal'
import { useGuildMember } from '@/services/GuildUserService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { ScreenType, useScreenType } from '@/utils/ScreenUtils'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'

export const GuildVotingView: React.FC = () => {
  const [showModal, setShowModal] = useState(false)
  const track = useSafeTrack()
  const { isGuildMember, loading } = useGuildMember()
  const { t } = useTranslate('guild-voting')
  const welcomeUrl =
    'https://content.angelstudios.com/guild/voting/ANGEL_101_VIDEO_3_ALY_V6_EVERGREENISH_H264_16X9_2024-8-28_CJ.mp4'

  const handleClose = () => {
    setShowModal(false)
  }

  const screenType = useScreenType()

  const votingMobileBackgroundUrl = getCloudinaryImageUrl({
    path: '/v1720468166/angel-studios/vote/Background_Image_2.png',
    transforms: 'q_auto:best',
  })

  const votingDesktopBackgroundUrl = getCloudinaryImageUrl({
    path: '/v1720467399/angel-studios/vote/show-wall-mock-01-230816_2.png',
    transforms: 'q_auto:best',
  })

  const trackAppLink = (partner: string) => {
    track('Clicked Voting App Link', {
      eventName: 'Clicked Voting App Link',
      partner,
      category: 'where-to-watch',
    })
  }

  const trackJoinGuildLink = () => {
    track('Guild Voting Join Guild CTA Clicked', {
      eventName: 'Clicked Join Guild Link',
      category: 'join-guild',
    })
  }

  const deviceLogoHeight = screenType === ScreenType.mobile || screenType === ScreenType.mobileplus ? 20 : 30

  return (
    <main className="relative h-full w-full bg-core-gray-950 text-white">
      <div className="relative flex h-full w-full py-16 lg:min-h-screen lg:items-center lg:pt-20">
        <ResponsiveBackgroundImage
          src={votingDesktopBackgroundUrl}
          srcUrls={{
            xs: votingMobileBackgroundUrl,
            sm: votingMobileBackgroundUrl,
            md: votingDesktopBackgroundUrl,
            lg: votingDesktopBackgroundUrl,
            xl: votingDesktopBackgroundUrl,
            '2xl': votingDesktopBackgroundUrl,
            '3xl': votingDesktopBackgroundUrl,
          }}
          preBackgroundStyles="linear-gradient(180deg, rgba(21, 21, 21, 0.3) 50%, #151515 100%), "
          className="pointer-events-none touch-none select-none bg-cover bg-top md:bg-center lg:bg-left-top"
        />
        <section className="relative flex w-full flex-col gap-20 px-4 md:px-[66px] xl:flex-row xl:justify-between 3xl:mx-auto 3xl:max-w-[1810px]">
          <div className="flex w-full flex-col gap-4 py-5 md:py-12 lg:py-20 xl:gap-10 3xl:gap-16">
            <HeadingXS
              weight="bold"
              className="md:photon-heading-md lg:photon-heading-lg xl:photon-heading-xl 2xl:photon-display md:max-w-[450px] lg:max-w-[588px] 2xl:max-w-[700px]"
            >
              {t('youDecideShows', 'You decide what shows are worth making.')}
            </HeadingXS>
            <div className="flex flex-col items-center justify-center gap-2 md:items-start md:gap-3 lg:gap-4 xl:hidden">
              <div className="relative cursor-pointer" onClick={() => setShowModal(true)}>
                <Image
                  src={getCloudinaryImageUrl({
                    path: '/v1724878452/angel-studios/guild/voting/What_is_the_Angel_Guild.webp',
                    transforms: 'q_auto:best',
                  })}
                  alt={t('whatIsTheAngelGuild', 'Voting Introduction: What is the Angel Guild?')}
                  height={195}
                  width={364}
                  priority
                  className="pointer-events-none rounded-2xl lg:min-h-[289px] lg:min-w-[538px]"
                />
                <PlayCircleIcon className="absolute left-1/2 top-1/2 z-10 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full bg-core-gray-950/70" />
              </div>
              <CaptionSM weight="medium" className="lg:photon-title-xs xl:photon-title-sm">
                {t('whatIsTheAngelGuild', 'Voting Introduction: What is the Angel Guild?')}
              </CaptionSM>
            </div>
            <div className="flex flex-col gap-4">
              <ParagraphSM weight="bold" className="lg:photon-paragraph-md">
                {t('howVotingBringsNew', 'How voting brings new movies and shows')}:
              </ParagraphSM>
              <div className="flex flex-col gap-2 lg:gap-3">
                <div className="flex flex-row items-center gap-2 text-core-gray-400">
                  <CircleCheckIcon size={16} color1="core-gray-400" color2="core-gray-950" />
                  <CaptionMD className="se:photon-caption-sm xl:photon-caption-lg">
                    {t('watchATorch', 'Watch a torch')}.
                  </CaptionMD>
                </div>
                <div className="flex flex-row items-center gap-2 text-core-gray-400">
                  <CircleCheckIcon size={16} color1="core-gray-400" color2="core-gray-950" />
                  <CaptionMD className="se:photon-caption-sm xl:photon-caption-lg">
                    {t('castYourVote', 'Cast your vote')}.
                  </CaptionMD>
                </div>
                <div className="flex flex-row items-center gap-2 text-core-gray-400">
                  <CircleCheckIcon size={16} color1="core-gray-400" color2="core-gray-950" />
                  <CaptionMD className="se:photon-caption-sm xl:photon-caption-lg">
                    {t('leaveFeedbackFilmmaker', 'Leave feedback for the filmmaker')}.
                  </CaptionMD>
                </div>
                <div className="flex flex-row items-center gap-2 text-core-gray-400">
                  <CircleCheckIcon size={16} color1="core-gray-400" color2="core-gray-950" />
                  <CaptionMD className="se:photon-caption-sm xl:photon-caption-lg">
                    {t('viewVotingResults', 'View voting results and track future progress')}.
                  </CaptionMD>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <ParagraphSM weight="bold" className="xl:photon-paragraph-md md:mt-2">
                {t('availableOn', 'Available on')}:
              </ParagraphSM>
              <div className="flex w-full flex-col justify-between gap-4">
                <LazyHydrate whenVisible>
                  <div className="flex w-fit flex-row flex-wrap items-center gap-4 lg:gap-8 2xl:gap-12">
                    <When condition={!isMobile}>
                      <a
                        href={paths.external.appleAppStore}
                        target="_blank"
                        rel="noreferrer noopener"
                        onClick={() => trackAppLink('apple-store')}
                      >
                        <AppleStoreMinimalLogo height={deviceLogoHeight} />
                      </a>
                      <a
                        href={paths.external.googlePlayStore}
                        target="_blank"
                        rel="noreferrer noopener"
                        onClick={() => trackAppLink('google-play')}
                      >
                        <GooglePlayMinimalLogo height={deviceLogoHeight} />
                      </a>
                    </When>
                    <a
                      href={paths.external.roku}
                      target="_blank"
                      rel="noreferrer noopener"
                      onClick={() => trackAppLink('roku')}
                    >
                      <RokuLogo height={deviceLogoHeight} />
                    </a>
                    <a
                      href={paths.external.appleTv}
                      target="_blank"
                      rel="noreferrer noopener"
                      onClick={() => trackAppLink('apple-tv')}
                    >
                      <AppleTvLogo height={deviceLogoHeight} />
                    </a>
                    <a
                      href={paths.external.fireTv}
                      target="_blank"
                      rel="noreferrer noopener"
                      onClick={() => trackAppLink('fire-tv')}
                    >
                      <FireTVLogo height={deviceLogoHeight} />
                    </a>
                    <a
                      href={paths.external.googleTv}
                      target="_blank"
                      rel="noreferrer noopener"
                      onClick={() => trackAppLink('google-tv')}
                    >
                      <GoogleTvLogoMono height={deviceLogoHeight} />
                    </a>
                    <a
                      href={paths.external.samsungTv}
                      target="_blank"
                      rel="noreferrer noopener"
                      onClick={() => trackAppLink('samsung-tv')}
                    >
                      <SamsungLogo height={deviceLogoHeight} />
                    </a>
                    <a
                      href={paths.external.lgTv}
                      target="_blank"
                      rel="noreferrer noopener"
                      onClick={() => trackAppLink('lg-tv')}
                    >
                      <LgTvLogo height={deviceLogoHeight} />
                    </a>
                    <a
                      href={paths.external.microsoft}
                      target="_blank"
                      rel="noreferrer noopener"
                      onClick={() => trackAppLink('microsoft-xbox')}
                    >
                      <MicrosoftXboxTextLogo height={deviceLogoHeight} />
                    </a>
                  </div>
                </LazyHydrate>
                <LazyHydrate whenVisible>
                  <When condition={isMobile && (isAndroid || isIOS)}>
                    <div>
                      {isIOS && (
                        <a
                          href={paths.external.appleAppStore}
                          target="_blank"
                          rel="noreferrer noopener"
                          onClick={() => trackAppLink('apple-store')}
                        >
                          <AppleStoreDownloadLogo height={40} />
                        </a>
                      )}
                      {isAndroid && (
                        <a
                          href={paths.external.googlePlayStore}
                          target="_blank"
                          rel="noreferrer noopener"
                          onClick={() => trackAppLink('google-play')}
                        >
                          <GoogleStoreDownloadBWLogo height={40} />
                        </a>
                      )}
                    </div>
                  </When>
                </LazyHydrate>
              </div>
            </div>
            <LazyHydrate whenVisible>
              <>
                <When condition={!loading}>
                  <When condition={!isGuildMember}>
                    <Transition
                      show
                      appear
                      as="div"
                      enter="transition-opacity duration-1000"
                      enterFrom="opacity-0"
                      enterTo="opacity-100"
                      leave="transition-opacity duration-1000"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <LinkButton onClick={trackJoinGuildLink} variant="oxide" href={`/guild/join`} className="w-fit">
                        {t('joinGuild', 'Join The Guild')}
                      </LinkButton>
                    </Transition>
                  </When>
                  <When condition={isGuildMember}>
                    <Transition
                      show
                      appear
                      as="div"
                      enter="transition-opacity duration-1000"
                      enterFrom="opacity-0"
                      enterTo="opacity-100"
                      leave="transition-opacity duration-1000"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <LinkButton variant="oxide" href={`/guild`} className="w-fit">
                        {t('myBenefits', 'My Benefits')}
                      </LinkButton>
                    </Transition>
                  </When>
                </When>
                <When condition={loading}>
                  <div className="h-[42px] w-[140px]" />
                </When>
              </>
            </LazyHydrate>
          </div>
          <div className="hidden flex-col items-center justify-center gap-2 md:gap-3 lg:gap-4 xl:flex">
            <div className="relative cursor-pointer" onClick={() => setShowModal(true)}>
              <Image
                src={getCloudinaryImageUrl({
                  path: '/v1724878452/angel-studios/guild/voting/What_is_the_Angel_Guild.webp',
                  transforms: 'q_auto:best',
                })}
                alt={t('whatIsTheAngelGuild', 'Voting Introduction: What is the Angel Guild?')}
                height={195}
                width={364}
                priority
                className="pointer-events-none rounded-2xl lg:min-h-[289px] lg:min-w-[538px]"
              />
              <PlayCircleIcon className="absolute left-1/2 top-1/2 z-10 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full bg-core-gray-950/70" />
            </div>
            <CaptionSM weight="medium" className="lg:photon-title-xs xl:photon-title-sm">
              {t('whatIsTheAngelGuild', 'Voting Introduction: What is the Angel Guild?')}
            </CaptionSM>
          </div>
        </section>
        <VideoModal showModal={showModal} videoUrl={welcomeUrl} handleClose={handleClose} showButton={false} />
      </div>
    </main>
  )
}
